import { z } from "zod"

import { powurOsComputerDataSchema } from "./computer-data"
import { powurOSEquipmentAssociationSchema } from "./equipment-association"
import { powurOsEquipmentCostsSchema } from "./equipment-cost"
import { powurOsEquipmentTypeSchema } from "./equipment-type"
import { powurOSInverterDataSchema } from "./inverter-data"
import { powurOsManufacturerSchema } from "./manufacturer"
import {
  powurOsEnergyBackupDataSchema,
  powurOsModuleDataSchema,
} from "./module-data"
import { powurOsProposalPriceSchema } from "./proposal-price"
import { powurOsWarrantySchema } from "./warranty"

export const basicAdderAssociationSchema = z.object({
  adderId: z.number().int(),
  equipmentId: z.number().int(),
  qty: z.number().int().optional().default(1),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
})

export const basicAdderAssociationsSchema = z.array(basicAdderAssociationSchema)

export type BasicAdderAssociation = z.infer<typeof basicAdderAssociationSchema>

export const powurOsCostSchema = z.object({
  id: z.number(),
  amount: z.number(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  equipmentId: z.number(),
  createdById: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdDateTime: z.string().nullable(),
  modifiedById: z.string().nullable(),
  modifiedBy: z.string().nullable(),
  modifiedDateTime: z.string().nullable(),
  deletedById: z.string().nullable(),
  deletedBy: z.string().nullable(),
  deletedDateTime: z.string().nullable(),
})

export const powurOsAllowedUtilitySchema = z.object({
  genabilityId: z.number(),
  name: z.string(),
})
export type PowurOsEquipmentAllowedUtility = z.infer<
  typeof powurOsAllowedUtilitySchema
>

export const powurOsEquipmentDistributorsSchema = z.object({
  distributorId: z.number().int(),
  catalogNumber: z.string(),
})

export const powurOsEquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  catalogNumber: z.string().optional(),
  manufacturerId: z.number().int(),
  manufacturer: powurOsManufacturerSchema,
  wattage: z.number(),
  equipmentTypeId: z.number().int(),
  equipmentType: powurOsEquipmentTypeSchema,
  equipmentCosts: z.array(powurOsEquipmentCostsSchema),
  currentCost: z.union([powurOsCostSchema, z.number()]).nullable(),
  warranty: z.nullable(powurOsWarrantySchema),
  parentEquipmentAssociations: z.union([
    z.null(),
    z.undefined(),
    z.array(powurOSEquipmentAssociationSchema),
  ]),
  childEquipmentAssociations: z
    .array(powurOSEquipmentAssociationSchema.nullable())
    .nullable()
    .default([]),
  height: z.number(),
  width: z.number(),
  moduleDataId: z.nullable(z.number().int()),
  moduleData: z.nullable(powurOsModuleDataSchema),
  computerData: powurOsComputerDataSchema.nullish(),
  inverterDataId: z.nullable(z.number().int()),
  inverterData: powurOSInverterDataSchema,
  energyBackupDataId: z.nullable(z.number().int()).optional(),
  energyBackupData: z.nullable(powurOsEnergyBackupDataSchema).optional(),
  proposalPrices: z.array(powurOsProposalPriceSchema),
  currentProposalPrice: z
    .nullable(z.union([powurOsCostSchema, z.number()]))
    .optional(),
  description: z.nullable(z.string()),
  basicAdderAssociations: basicAdderAssociationsSchema,
  compatibleInverterManufacturers: z
    .array(
      z.object({
        equipmentId: z.number().int(),
        manufacturerId: z.number().int(),
        manufacturer: powurOsManufacturerSchema,
      }),
    )
    .optional(),
  cecWeightedEfficiency: z.number(),
  createdById: z.nullable(z.string()),
  createdDateTime: z.nullable(z.string()),
  modifiedById: z.nullable(z.string()),
  modifiedDateTime: z.nullable(z.string()),
  deletedById: z.nullable(z.string()),
  isKit: z.boolean().optional(),
  deletedDateTime: z.nullable(z.string()),
  allowedUtilities: z.union([
    z.null(),
    z.undefined(),
    z.array(powurOsAllowedUtilitySchema),
  ]),
  equipmentDistributors: z.nullable(
    z.array(powurOsEquipmentDistributorsSchema).optional(),
  ),
  manufacturerNumber: z.nullable(z.string()),
})
export type PowurOsEquipment = z.infer<typeof powurOsEquipmentSchema>
